import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { popupWindowFeatures } from "../../lib/popupWindow";
import { dealerSetupUrl } from "../../lib/url";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";

/** Names the popup so repeat clicks refocus the same window instead of opening another. */
const DEALER_SETUP_WINDOW_NAME = "dsc-dealer-setup";

// TopBar button opening the CAPS Dealer Setup page in its own popup window
function DealerSetup() {
  const { t } = useTranslation();
  const [errorOpen, setErrorOpen] = useState(false);

  const handleClick = () => {
    const url = dealerSetupUrl();
    // Empty when CAPS has no origin for the resolved environment (e.g. local dev
    // outside the CAPS frame) — opening that would just yield a blank window.
    if (!url) {
      setErrorOpen(true);
      return;
    }
    // No `noopener`: it forces a tab in some browsers and drops the Window
    // handle needed for focus()/named-window reuse. Same first-party CAPS origin.
    globalThis.open(url, DEALER_SETUP_WINDOW_NAME, popupWindowFeatures())?.focus();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="text-foreground hover:text-primary flex cursor-pointer items-center gap-1 transition-colors"
      >
        {t("topBar.dealerSetup")}
      </button>

      <Dialog open={errorOpen} onOpenChange={setErrorOpen}>
        <DialogContent showCloseButton={false} className="gap-0 overflow-hidden p-0 sm:max-w-sm">
          <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
            <DialogTitle className="text-primary-foreground text-sm font-semibold">
              {t("topBar.dealerSetup")}
            </DialogTitle>
            <DialogClose
              className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors"
              aria-label={t("topBar.dealerSetupClose")}
            >
              <X className="size-4.5" aria-hidden="true" />
            </DialogClose>
          </div>

          <div className="space-y-4 p-4">
            {/* The visible message doubles as the Radix description — Radix warns
                when DialogContent has no Description, and a separate sr-only copy
                would just duplicate this text for screen readers. */}
            <DialogDescription className="text-foreground text-sm">
              {t("topBar.dealerSetupUnavailable")}
            </DialogDescription>
            <div className="flex justify-end">
              <DialogClose asChild>
                <Button type="button" size="sm">
                  {t("topBar.dealerSetupOk")}
                </Button>
              </DialogClose>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DealerSetup;
